import axios from "axios";
import { useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { BackForeColor } from "../../components/common/BackForeColor";
import localize from "../../common/localize";
import ErrorMessage from "../../components/common/ErrorMessage";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import TableComponent from "../../components/common/TableComponent";
import PagePagination from "../../components/PagePagination";
import ActionButtons from "../../components/common/ActionButtons";
import ValueOrNoDataLabel from "../../components/common/ValueOrNoDataLabel";
import ModalBackground from "../../components/common/ModalBackground";
import {
  generateNoDataValues,
  getBackColor,
  getFiltersInfo,
  getOperationalStatusColorById,
  getOperationalStatusForeColorById,
  getOperationalStatusNameFromId,
  OperationalStatusEnum,
} from "../../common/utils";
import useGlobalContext from "../../hooks/useGlobalContext";
import {
  defaultFilteringForPreOrders,
  getPreOrderFilterOptions,
  getUserName,
} from "../../common/EntitiesManagement";
import PreOrderSummary from "./components/PreOrderSummary";
import { getInputLabelProps, getInputProps } from "../../common/appStyles";
import PreOrderStatusAssignment from "./components/PreOrderStatusAssignment";
import { Refresh, Settings } from "@mui/icons-material";
import FilterWithPrinter from "../../components/common/FilterWithPrinter";
import EntityPrinter from "../../components/common/EntityPrinter";

export default function PreOrders() {
  const {
    filterForPreOrders,
    setFilterForPreOrders,
    preOrderData,
    PREORDER_ENDPOINT,
    config,
    pageIndexPreOrders,
    setPageIndexPreOrders,
  } = useGlobalContext();

  const [errorMessage, setErrorMessage] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showStatusAssignmentModal, setShowStatusAssignmentModal] =
    useState(false);
  const [selectedPreOrder, setSelectedPreOrder] = useState(undefined);
  const [orderId, setOrderId] = useState("");
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successNotificationMessage, setSuccessNotificationMessage] =
    useState("");
  const [newStatus, setNewStatus] = useState(undefined);
  const [newStatusObservation, setNewStatusObservation] = useState("");
  const [showOrderCreationConfirmation, setShowOrderCreationConfirmation] =
    useState(false);
  const [showPrinterModal, setShowPrinterModal] = useState(false);

  const assignOrderToPreOrder = async () => {
    try {
      const result = await axios.put(
        `${PREORDER_ENDPOINT}/${selectedPreOrder.Id}/order`,
        { EventId: orderId },
        config
      );
      setShowEditModal(false);

      if (validateOperationResult(result.data)) {
        setSuccessNotificationMessage(localize.order_assigned_successfully);
        setShowSuccessNotification(true);
        window.location.reload(false);
      }
    } catch (error) {
      setErrorMessage(error.message);
      setShowErrorModal(true);
    }
  };

  const assignStatusToPreOrder = async () => {
    try {
      const result = await axios.put(
        `${PREORDER_ENDPOINT}/${selectedPreOrder.Id}/assignStatus`,
        {
          StatusId: newStatus?.Id,
          Observation: newStatusObservation,
          StatusChangedBy: getUserName(),
        },
        config
      );
      setShowStatusAssignmentModal(false);

      if (validateOperationResult(result.data)) {
        setSuccessNotificationMessage(localize.status_assigned_successfully);
        setShowSuccessNotification(true);
      }
    } catch (error) {
      setErrorMessage(error.message);
      setShowErrorModal(true);
    }
  };

  const validateOperationResult = (result) => {
    if (!result.ok && !result.Ok) {
      setErrorMessage(
        JSON.stringify(result?.data || result?.Data || result?.error)
      );
      setShowErrorModal(true);
      return false;
    }

    return true;
  };

  const handleOrderIdChange = (e) => {
    setOrderId(e.target.value);
  };

  const canNotEditPreOrder = (preOrder) =>
    (preOrder.Event &&
      (preOrder.Event.CanEdit === 0 ||
        preOrder.Event.CurrentStatus?.IsFinal)) ||
    preOrder.CurrentStatus?.IsFinal;

  const createServiceOrder = async (preOrder) => {
    try {
      const result = await axios.post(
        `${PREORDER_ENDPOINT}/${preOrder.Id}/convertToOrder`,
        undefined,
        config
      );
      setShowSummaryModal(false);
      setShowOrderCreationConfirmation(false);

      if (validateOperationResult(result.data)) {
        setSuccessNotificationMessage(localize.order_assigned_successfully);
        setShowSuccessNotification(true);
      }
    } catch (error) {
      setErrorMessage(error.message);
      setShowSummaryModal(false);
      setShowOrderCreationConfirmation(false);
      setShowErrorModal(true);
    }
  };

  const getColumnTitles = () => {
    return [
      localize.pre_order,
      localize.status,
      localize.order_code,
      localize.client_name,
      localize.proceeding,
      localize.event_type,
      localize.reported_by,
      localize.creation_date,
      localize.last_update,
    ];
  };

  const showPrinter = () => {
    setShowPrinterModal(true);
  };

  const getEventStatusName = (item) => {
    if (!item.Event) {
      return item.CurrentStatus
        ? item.CurrentStatus?.Name
        : localize.quotation_status_pending;
    }

    if (item.Event?.OperationalStatus === OperationalStatusEnum.Canceled) {
      return getOperationalStatusNameFromId(OperationalStatusEnum.Canceled);
    }

    return item.Event?.CurrentStatus?.Name;
  };

  const getEventStatusBackgroundColor = (item) => {
    if (!item?.Event) {
      return item.CurrentStatus
        ? item.CurrentStatus?.BackgroundColor
        : "ligthgray";
    }

    if (item.Event?.OperationalStatus === OperationalStatusEnum.Canceled) {
      return getOperationalStatusForeColorById(OperationalStatusEnum.Canceled);
    }

    return item.Event?.CurrentStatus?.BackgroundColor;
  };

  const getEventStatusForegroundColor = (item) => {
    if (!item?.Event) {
      return item.CurrentStatus ? item.CurrentStatus?.ForegroundColor : "black";
    }

    if (item.Event?.OperationalStatus === OperationalStatusEnum.Canceled) {
      return getOperationalStatusColorById(OperationalStatusEnum.Canceled);
    }

    return item.Event?.CurrentStatus?.ForegroundColor;
  };

  return (
    <div className="lateral_padding">
      {showPrinterModal && (
        <EntityPrinter
          closeCallback={() => setShowPrinterModal(false)}
          title={localize.print}
          report_title={localize.pre_orders_title}
          report_subtitle={getFiltersInfo(filterForPreOrders)}
          url={PREORDER_ENDPOINT}
          filters={filterForPreOrders ?? []}
          columns={getColumnTitles()}
          objectTypeName="pre_order"
        />
      )}

      {showSuccessNotification && (
        <ModalBackground
          showModal={showSuccessNotification}
          setShowModal={setShowSuccessNotification}
          title={localize.process_succeeded}
          onClickToSubmit={() => {
            setShowSuccessNotification(false);
            window.location.reload(false);
          }}
          hideCancelButton
          okButtonText={localize.ok}
          Component={<Typography>{successNotificationMessage}</Typography>}
        />
      )}

      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showOrderCreationConfirmation && (
        <ModalBackground
          showModal={showOrderCreationConfirmation}
          setShowModal={setShowOrderCreationConfirmation}
          title={localize.service_order}
          okButtonText={localize.yes}
          cancelButtonText={localize.no}
          onClickToSubmit={() => {
            createServiceOrder(selectedPreOrder);
            setShowOrderCreationConfirmation(false);
          }}
          onClickToCancel={() => {
            setSelectedPreOrder(undefined);
            setShowOrderCreationConfirmation(false);
          }}
          Component={
            <Typography>{`${localize.question_create_order_from_preorder}: ${selectedPreOrder.Code}`}</Typography>
          }
        />
      )}

      {showSummaryModal && (
        <ModalBackground
          customSecondaryAction={
            selectedPreOrder?.Event || selectedPreOrder?.CurrentStatus?.IsFinal
              ? undefined
              : {
                  name: localize.create_service_order,
                  action: () => {
                    setShowSummaryModal(false);
                    setShowOrderCreationConfirmation(true);
                  },
                }
          }
          showModal={showSummaryModal}
          setShowModal={setShowSummaryModal}
          title={`${localize.pre_order} - ${selectedPreOrder.Code}`}
          onClickToSubmit={() => {
            setSelectedPreOrder(undefined);
            setShowSummaryModal(false);
          }}
          okButtonText={localize.ok}
          hideCancelButton
          Component={<PreOrderSummary preOrder={selectedPreOrder} />}
        />
      )}

      {showStatusAssignmentModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showStatusAssignmentModal}
          setShowModal={setShowStatusAssignmentModal}
          title={`${localize.assign_status_to} ${selectedPreOrder.Code}`}
          onClickToSubmit={() => {
            setShowStatusAssignmentModal(false);
            assignStatusToPreOrder();
          }}
          onClickToCancel={() => {
            setNewStatus(undefined);
            setNewStatusObservation("");
            setShowStatusAssignmentModal(false);
          }}
          Component={
            <PreOrderStatusAssignment
              eventStatus={newStatus}
              setEventStatus={setNewStatus}
              observation={newStatusObservation}
              setObservation={setNewStatusObservation}
            />
          }
          validators={
            !newStatus ||
            (newStatus.IsFinal &&
              (!newStatusObservation ||
                newStatusObservation.replaceAll(" ", "").length === 0))
          }
        />
      )}

      {showEditModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          title={`${localize.assign_service_order_to} ${selectedPreOrder.Code}`}
          onClickToSubmit={() => {
            setShowEditModal(false);
            assignOrderToPreOrder();
          }}
          onClickToCancel={() => {
            setOrderId("");
            setSelectedPreOrder(undefined);
            setShowEditModal(false);
          }}
          Component={
            <Box className="mt-2 w-full flex flex-col gap-2">
              <Alert className="w-full" severity="info">
                <label className="text-xs">{localize.assign_order_info}</label>
              </Alert>

              <TextField
                type="number"
                InputLabelProps={getInputLabelProps()}
                inputProps={getInputProps()}
                value={orderId}
                onChange={handleOrderIdChange}
                size="small"
                id="orderId"
                name="orderId"
                label={localize.order_number}
                variant="outlined"
                fullWidth
              />
            </Box>
          }
          validators={!orderId || orderId.replaceAll(" ", "").length === 0}
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.pre_orders_title}
            subtitle={localize.pre_orders_description}
          />

          <FilterWithPrinter
            filterItemNameValue="preorder_filter"
            filterDataValue={filterForPreOrders}
            setFilterDataValue={setFilterForPreOrders}
            defaultFilteringValue={defaultFilteringForPreOrders(true)}
            filterOptionsValue={getPreOrderFilterOptions()}
            lateral_padding
            printButtonMethod={showPrinter}
          />
        </CardContent>
      </Card>

      <TableComponent
        pagination={
          <PagePagination
            pageIndex={pageIndexPreOrders}
            setPageIndex={setPageIndexPreOrders}
            dataArray={preOrderData}
          />
        }
        firstColumnTitle={localize.actions}
        columnTitles={getColumnTitles()}
        results={
          preOrderData && preOrderData?.Data?.length > 0
            ? preOrderData.Data.map((item) => ({
                key: item.Id,
                data: [
                  <div>
                    <ActionButtons
                      hideEditButton
                      hideDeleteButton
                      detailsButtonText={localize.details}
                      onClickForDetailButton={() => {
                        setSelectedPreOrder(item);
                        setShowSummaryModal(true);
                      }}
                      buttons={
                        !canNotEditPreOrder(item)
                          ? [
                              {
                                name: `${localize.assign_event}`,
                                icon: (
                                  <Settings
                                    style={{ width: "16px", height: "16px" }}
                                  />
                                ),
                                action: () => {
                                  setOrderId("");
                                  setSelectedPreOrder(item);
                                  setShowEditModal(true);
                                },
                              },
                              {
                                name: `${localize.assign_status_to} ${item.Code}`,
                                icon: (
                                  <Refresh
                                    style={{ width: "16px", height: "16px" }}
                                  />
                                ),
                                action: () => {
                                  setSelectedPreOrder(item);
                                  setNewStatus(undefined);
                                  setNewStatusObservation("");
                                  setShowStatusAssignmentModal(true);
                                },
                              },
                            ]
                          : undefined
                      }
                    />
                  </div>,
                  item.Code,
                  <BackForeColor
                    backColor={getEventStatusBackgroundColor(item)}
                    foreColor={getEventStatusForegroundColor(item)}
                    text={getEventStatusName(item)}
                  />,
                  item.Event ? (
                    <Link
                      href={`/events/detail/${item.Event.Id}`}
                      target="_blank"
                    >
                      {item.Event?.Code}
                    </Link>
                  ) : (
                    <ValueOrNoDataLabel />
                  ),
                  <ValueOrNoDataLabel data={item.ClientName} />,
                  <ValueOrNoDataLabel data={item.ProductType?.Name} />,
                  <ValueOrNoDataLabel data={item.ProductBrand?.Name} />,
                  item.CreatedBy,
                  new Date(item.CreationDate).toLocaleString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                  new Date(
                    item.Event
                      ? item.Event.ModificationDate
                      : item.ModificationDate
                  ).toLocaleString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                ],
              }))
            : [generateNoDataValues(10)]
        }
      />
    </div>
  );
}
